import { createClient } from '@/utils/supabase/client';
import { logger } from "@/lib/logger";

const supabase = createClient();

// 全局 Supabase 客户端实例
let supabaseInstance = null;

// 防抖控制变量
let resetInProgress = false;
let resetTimeout = null;

/**
 * 获取全局 Supabase 客户端实例
 * 确保整个应用中只使用一个实例，避免多个 GoTrueClient 实例问题
 */
export function getSupabaseClient() {
  if (!supabaseInstance) {
    try {
      logger.info("Creating new Supabase client instance");
      supabaseInstance = supabase;
    } catch (error) {
      logger.error("Error creating Supabase client:", error);
      // 创建失败时返回一个空的客户端，避免应用崩溃
      return createEmptyClient();
    }
  }
  
  return supabaseInstance;
}

/**
 * 重置 Supabase 客户端实例
 * 在需要重新创建客户端时使用，例如在登录状态变化时
 * 使用防抖处理，避免短时间内多次重置
 */
export function resetSupabaseClient() {
  // 如果已经有重置操作在进行中，取消之前的操作
  if (resetTimeout) {
    clearTimeout(resetTimeout);
  }
  
  // 如果重置操作正在进行中，直接返回当前实例
  if (resetInProgress) {
    return supabaseInstance;
  }
  
  resetInProgress = true;
  
  // 使用 setTimeout 进行防抖，避免短时间内多次重置
  resetTimeout = setTimeout(() => {
    try {
      logger.info("Resetting Supabase client instance");
      supabaseInstance = null;
      supabaseInstance = getSupabaseClient();
      
      // 重置完成后，清除防抖控制变量
      resetInProgress = false;
      resetTimeout = null;
    } catch (error) {
      logger.error("Error resetting Supabase client:", error);
      resetInProgress = false;
      resetTimeout = null;
    }
  }, 300); // 300ms 防抖时间
  
  return supabaseInstance;
}

/**
 * 检查 Supabase 客户端实例是否已创建
 */
export function hasSupabaseClient() {
  return !!supabaseInstance;
}

/**
 * 创建一个空的客户端对象，用于在创建失败时返回
 * 避免应用崩溃
 */
function createEmptyClient() {
  return {
    auth: {
      getUser: async () => ({ data: { user: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      signOut: async () => ({ error: null })
    },
    from: () => ({
      select: () => ({ data: [], error: null }),
      insert: () => ({ data: null, error: null }),
      update: () => ({ data: null, error: null }),
      delete: () => ({ data: null, error: null }),
      eq: () => ({ data: null, error: null }),
      single: () => ({ data: null, error: null }),
      order: () => ({ data: [], error: null }),
      limit: () => ({ data: [], error: null })
    })
  };
}

export default supabase; 